import React, { useEffect, useState } from "react";
import { apiFetch } from "../api/client";

const emptyForm = {
  name: "",
  description: "",
  kiosk_enabled: false,
  usb_blocked: false,
  screenshot_disabled: false,
  allowed_apps: "",
  blocked_apps: "",
  allowed_urls: "",
};

function csvToArray(value) {
  if (!value) return [];
  return value
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

function arrayToCsv(value) {
  if (Array.isArray(value)) return value.join(", ");
  return value || "";
}

function PoliciesPage() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPolicyId, setEditingPolicyId] = useState(null);

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadPolicies();
  }, []);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      const res = await apiFetch(`/policies`);
      const data = res.ok ? await res.json() : [];
      setPolicies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Policies load error:", error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingPolicyId(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmitPolicy = async (e) => {
    e.preventDefault();

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      kiosk_enabled: form.kiosk_enabled,
      usb_blocked: form.usb_blocked,
      screenshot_disabled: form.screenshot_disabled,
      allowed_apps: csvToArray(form.allowed_apps),
      blocked_apps: csvToArray(form.blocked_apps),
      allowed_urls: csvToArray(form.allowed_urls),
    };

    try {
      const res = await apiFetch(
        editingPolicyId
          ? `/policies/${editingPolicyId}`
          : `/policies`,
        {
          method: editingPolicyId ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(
          data.detail ||
            data.error ||
            `Failed to ${editingPolicyId ? "update" : "create"} policy`
        );
      }

      resetForm();
      await loadPolicies();
      alert(editingPolicyId ? "Policy updated successfully" : "Policy created successfully");
    } catch (error) {
      console.error("Policy submit error:", error);
      alert(error.message);
    }
  };

  const handleEditPolicy = (policy) => {
    setEditingPolicyId(policy.id);

    setForm({
      name: policy.name || "",
      description: policy.description || "",
      kiosk_enabled: !!policy.kiosk_enabled,
      usb_blocked: !!policy.usb_blocked,
      screenshot_disabled: !!policy.screenshot_disabled,
      allowed_apps: arrayToCsv(policy.allowed_apps),
      blocked_apps: arrayToCsv(policy.blocked_apps),
      allowed_urls: arrayToCsv(policy.allowed_urls),
    });

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeletePolicy = async (policyId) => {
    const confirmed = window.confirm("Delete this policy?");
    if (!confirmed) return;

    try {
      const res = await apiFetch(`/policies/${policyId}`, {
        method: "DELETE",
      });

      const data = await res.json();

      if (!res.ok || data.error) {
        throw new Error(data.detail || data.error || "Failed to delete policy");
      }

      if (editingPolicyId === policyId) {
        resetForm();
      }

      await loadPolicies();
      alert("Policy deleted successfully");
    } catch (error) {
      console.error("Delete policy error:", error);
      alert(error.message);
    }
  };

  return (
    <div>
      <div className="page-actions">
        <h2>Policies</h2>
      </div>

      <div className="card">
        <h3>{editingPolicyId ? "Edit Policy" : "Create Policy"}</h3>

        <form onSubmit={handleSubmitPolicy} className="policy-form">
          <input
            type="text"
            name="name"
            placeholder="Policy name"
            value={form.name}
            onChange={handleChange}
            className="filter-input"
            required
          />

          <input
            type="text"
            name="description"
            placeholder="Description"
            value={form.description}
            onChange={handleChange}
            className="filter-input"
          />

          <input
            type="text"
            name="allowed_apps"
            placeholder="Allowed apps (comma separated)"
            value={form.allowed_apps}
            onChange={handleChange}
            className="filter-input"
          />

          <input
            type="text"
            name="blocked_apps"
            placeholder="Blocked apps (comma separated)"
            value={form.blocked_apps}
            onChange={handleChange}
            className="filter-input"
          />

          <input
            type="text"
            name="allowed_urls"
            placeholder="Allowed URLs (comma separated)"
            value={form.allowed_urls}
            onChange={handleChange}
            className="filter-input"
          />

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="kiosk_enabled"
              checked={form.kiosk_enabled}
              onChange={handleChange}
            />
            Kiosk Enabled
          </label>

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="usb_blocked"
              checked={form.usb_blocked}
              onChange={handleChange}
            />
            USB Blocked
          </label>

          <label className="checkbox-row">
            <input
              type="checkbox"
              name="screenshot_disabled"
              checked={form.screenshot_disabled}
              onChange={handleChange}
            />
            Screenshot Disabled
          </label>

          <div style={{ display: "flex", gap: "10px", marginTop: "12px" }}>
            <button type="submit" className="primary-button">
              {editingPolicyId ? "Update Policy" : "Create Policy"}
            </button>

            {editingPolicyId && (
              <button
                type="button"
                className="secondary-button"
                onClick={resetForm}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Kiosk</th>
              <th>USB</th>
              <th>Screenshot</th>
              <th>Allowed Apps</th>
              <th>Blocked Apps</th>
              <th>Assigned Devices</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="9">Loading policies...</td>
              </tr>
            ) : policies.length > 0 ? (
              policies.map((policy) => (
                <tr key={policy.id}>
                  <td>{policy.name}</td>
                  <td>{policy.description || "N/A"}</td>
                  <td>{policy.kiosk_enabled ? "Enabled" : "Disabled"}</td>
                  <td>{policy.usb_blocked ? "Blocked" : "Allowed"}</td>
                  <td>{policy.screenshot_disabled ? "Disabled" : "Allowed"}</td>
                  <td>{arrayToCsv(policy.allowed_apps) || "N/A"}</td>
                  <td>{arrayToCsv(policy.blocked_apps) || "N/A"}</td>
                  <td>{policy.assigned_devices_count || 0}</td>
                  <td>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        type="button"
                        onClick={() => handleEditPolicy(policy)}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDeletePolicy(policy.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9">No policies found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PoliciesPage;